import { useEffect, useRef } from "react";
import { Clock, MapPin, Play } from "lucide-react";
import type { Artist } from "./ArtistCard";

const CSS = `
.lus{--ink:#1c1c1e;--ink2:#3a3a3c;--muted:#8e8e93;--sep:rgba(60,60,67,.12);--blue:#389fba;--gold:#f7b41c;--seafoam:#6eca9f;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",Roboto,Helvetica,Arial,sans-serif}
.lus-back{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:99998;animation:lusFade .3s ease}
@keyframes lusFade{from{opacity:0}to{opacity:1}}
.lus{position:fixed;left:0;right:0;bottom:0;z-index:99999;background:#fff;color:var(--ink);border-radius:24px 24px 0 0;max-height:90vh;max-height:88svh;overflow:auto;max-width:600px;margin:0 auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;padding-bottom:calc(24px + env(safe-area-inset-bottom));animation:lusUp .4s cubic-bezier(.32,.72,0,1)}
@keyframes lusUp{from{transform:translateY(100%)}to{transform:none}}
.lus *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
.lus .grabber{width:38px;height:5px;border-radius:3px;background:rgba(60,60,67,.2);margin:9px auto 4px}
.lus-close{position:sticky;top:0;float:right;margin:-2px 14px 0 0;width:30px;height:30px;border:0;border-radius:50%;background:rgba(118,118,128,.16);color:var(--ink2);font-size:16px;line-height:1;cursor:pointer;z-index:3}
.lus-img{width:100%;aspect-ratio:1/1;object-fit:cover;object-position:center 22%;display:block;max-height:46vh;max-height:42svh;background:#dfe3e6}
.lus-body{padding:16px 20px 4px}
.lus-meta{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 12px}
.lus-pill{display:inline-flex;align-items:center;gap:5px;font-size:13px;font-weight:600;border-radius:100px;padding:6px 11px}
.lus-pill.stage{background:rgba(56,159,186,.12);color:#25708a}
.lus-pill.time{background:rgba(247,180,28,.16);color:#8a6400}
.lus-pill.day{background:#f2f2f7;color:var(--ink2)}
.lus-body .nm{font-family:"Playfair Display",Georgia,serif;font-weight:700;font-size:27px;line-height:1.08;margin:0}
.lus-body .rl{color:var(--blue);font-weight:600;font-size:14.5px;margin:4px 0 0}
.lus h4{font-size:12px;letter-spacing:.13em;text-transform:uppercase;color:var(--muted);margin:20px 0 9px}
.lus .bio{font-size:15px;color:var(--ink2);line-height:1.55;margin:0}
.lus .songs{list-style:none;margin:0;padding:0}
.lus .songs li{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:.5px solid var(--sep);font-size:15px;color:var(--ink2)}
.lus .songs li:last-child{border-bottom:0}
.lus .songs li svg{flex:0 0 auto;color:var(--seafoam)}
.lus .spot{display:inline-block;margin-top:14px;font-size:14px;font-weight:600;color:var(--blue);text-decoration:none}
.lus .spot:hover{text-decoration:underline}
@media(min-width:768px){
  .lus-back{display:grid;place-items:center;padding:28px}
  .lus{position:relative;left:auto;right:auto;bottom:auto;margin:0;width:min(600px,100%);max-height:86vh;border-radius:22px;padding-bottom:26px;animation:lusPop .34s cubic-bezier(.32,.72,0,1)}
  .lus .grabber{display:none}
  .lus-img{aspect-ratio:16/10;max-height:none;object-position:center 25%}
  .lus-body{padding:20px 26px 4px}
  .lus-body .nm{font-size:32px}
}
@media (prefers-reduced-motion:reduce){.lus,.lus-back{animation:none!important}}
`;

interface LineupSheetProps {
  artist: Artist;
  onClose: () => void;
}

const LineupSheet = ({ artist, onClose }: LineupSheetProps) => {
  const sheetRef = useRef<HTMLDivElement | null>(null);

  // Load the editorial font once (matches the What-to-Wear post).
  useEffect(() => {
    const id = "lus-playfair";
    if (!document.getElementById(id)) {
      const l = document.createElement("link");
      l.id = id;
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  // Freeze the background while the sheet is open (no layout shift), block
  // pull-to-refresh, and allow scrolling only inside the sheet.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOB = html.style.overscrollBehavior;
    const prevBodyOB = body.style.overscrollBehavior;
    const prevBodyOverflow = body.style.overflow;
    html.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    const blockBg = (e: TouchEvent) => {
      const s = sheetRef.current;
      if (s && s.contains(e.target as Node)) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", blockBg, { passive: false });
    return () => {
      html.style.overscrollBehavior = prevHtmlOB;
      body.style.overscrollBehavior = prevBodyOB;
      body.style.overflow = prevBodyOverflow;
      document.removeEventListener("touchmove", blockBg);
    };
  }, []);

  // Swipe-down-to-dismiss (mobile). Only engages from the top handle area or
  // when the sheet is scrolled to the top, so reading the details scrolls fine.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    let startY = 0, drag = 0, fromHandle = false;
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    const onStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      drag = 0;
      fromHandle = startY - el.getBoundingClientRect().top < 72;
    };
    const onMove = (e: TouchEvent) => {
      const dy = e.touches[0].clientY - startY;
      const canDismiss = fromHandle || el.scrollTop <= 0;
      if (!canDismiss || dy <= 0) {
        if (drag > 0) { el.style.transition = ""; el.style.transform = ""; drag = 0; }
        return;
      }
      drag = dy;
      el.style.transition = "none";
      el.style.transform = `translateY(${dy}px)`;
      e.preventDefault();
    };
    const onEnd = () => {
      if (drag <= 0) return;
      if (drag > 80) {
        el.style.transition = "transform .24s ease-in";
        el.style.transform = "translateY(100%)";
        closeTimer = setTimeout(onClose, 220);
      } else {
        el.style.transition = "transform .28s cubic-bezier(.32,.72,0,1)";
        el.style.transform = "";
      }
      drag = 0;
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      if (closeTimer) clearTimeout(closeTimer);
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="lus-back" onClick={onClose}>
        <div
          className="lus"
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-label={`${artist.name} details`}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="lus-close" onClick={onClose} aria-label="Close">✕</button>
          <div className="grabber" />
          {artist.image && <img className="lus-img" src={artist.image} alt={artist.name} />}
          <div className="lus-body">
            <div className="lus-meta">
              {artist.day && <span className="lus-pill day">{artist.day}</span>}
              {artist.time && (
                <span className="lus-pill time"><Clock size={13} strokeWidth={2.5} />{artist.time}</span>
              )}
              {artist.stage && (
                <span className="lus-pill stage"><MapPin size={13} strokeWidth={2.5} />{artist.stage}</span>
              )}
            </div>

            <p className="nm">{artist.name}</p>
            {artist.role && <p className="rl">{artist.role}</p>}

            {artist.bio && (
              <>
                <h4>About</h4>
                <p className="bio">{artist.bio}</p>
              </>
            )}

            {artist.topSongs && artist.topSongs.length > 0 && (
              <>
                <h4>Known For</h4>
                <ul className="songs">
                  {artist.topSongs.map((song) => (
                    <li key={song}><Play size={14} strokeWidth={2.5} />{song}</li>
                  ))}
                </ul>
              </>
            )}

            {artist.spotifyUrl && (
              <a className="spot" href={artist.spotifyUrl} target="_blank" rel="noopener noreferrer">
                Listen on Spotify ›
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LineupSheet;
